/**
 * @module components/analysis/LearnMoreTip
 * @description
 *   Tooltip positioned northeast of the trigger. Portal-rendered onto
 *   document.body so parents with overflow:hidden (cards, panels) do not clip it.
 *
 *   Compact (max-lg / COMPACT_MAX_LG): tap trigger to toggle; outside pointerdown
 *   closes. No mouseenter open (avoids sticky hover after tap).
 *   Desktop: hover/focus only — no click pin.
 *
 *   Shared by:
 *   - Technical Analysis ScoreGrid signal labels (label + info icon)
 *   - Genre fit Axis fit rows (label + info icon) — desktop only; compact uses
 *     inline axis definitions in GenreFitLearnMore
 *   - Sonic neighbor chips (custom children trigger, no icon)
 *
 *   Cursor: default for hover-only tips; pass cursor="pointer" when the trigger
 *   is part of a clickable control (Genre profiles Axis fit rows).
 *
 *   If definition is null, renders children or a plain truncated label.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import useMediaQuery, { COMPACT_MAX_LG } from '../../hooks/useMediaQuery';

/**
 * @param {object} props
 * @param {string} props.id
 * @param {string} props.label
 * @param {string|null} props.definition
 * @param {string} [props.labelClassName]
 * @param {React.ReactNode} [props.children] custom trigger (e.g. Chip); skips info icon
 * @param {string} [props.className] wrapper class when using children
 * @param {'default'|'pointer'} [props.cursor='default'] pointer only when trigger itself is clickable
 */
export default function LearnMoreTip({
  id,
  label,
  definition,
  labelClassName = 'text-sm text-critic-text-primary',
  children = null,
  className = '',
  cursor = 'default',
}) {
  const triggerRef = useRef(null);
  const tipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const isCompact = useMediaQuery(COMPACT_MAX_LG);
  const tipDomId = `learn-more-tip-${id}`;
  const cursorClass = cursor === 'pointer' ? 'cursor-pointer' : 'cursor-default';

  const showTip = () => setOpen(true);
  const hideTip = () => setOpen(false);
  const toggleTip = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((prev) => !prev);
  };

  // Compact: outside pointerdown closes (same idea as Genre Fit axis click-away).
  useEffect(() => {
    if (!isCompact || !open) return undefined;

    const onPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (triggerRef.current?.contains(target)) return;
      if (tipRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isCompact, open]);

  // Close if we leave compact (resize to desktop) so hover state can take over cleanly.
  useEffect(() => {
    if (!isCompact) setOpen(false);
  }, [isCompact]);

  // Reposition on open / scroll / resize so the tip stays near the trigger.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;

    const place = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const tip = tipRef.current;
      const tipWidth = tip?.offsetWidth || 240;
      const tipHeight = tip?.offsetHeight || 72;
      const gap = 8;
      // Prefer northeast (above + near right edge); flip below if clipped.
      let left = rect.right - tipWidth * 0.25;
      let top = rect.top - tipHeight - gap;
      left = Math.min(left, window.innerWidth - tipWidth - 12);
      left = Math.max(12, left);
      if (top < 12) {
        top = rect.bottom + gap;
      }
      setCoords({ top, left });
    };

    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, definition]);

  if (!definition) {
    if (children) return children;
    return <span className={`${labelClassName} truncate`}>{label}</span>;
  }

  const tipPortal =
    open &&
    createPortal(
      <div
        ref={tipRef}
        id={tipDomId}
        role="tooltip"
        data-learn-more-tip={id}
        style={{ top: coords.top, left: coords.left }}
        className="fixed z-[100] w-60 max-w-[min(15rem,calc(100vw-1.5rem))] rounded-xl border border-white/[0.12] bg-critic-bg-deep/95 backdrop-blur-xl px-3 py-2.5 shadow-lg pointer-events-none"
      >
        <p className="text-xs font-medium text-critic-text-primary mb-1">{label}</p>
        <p className="text-xs leading-relaxed text-critic-text-secondary">{definition}</p>
      </div>,
      document.body
    );

  // Desktop: hover/focus. Compact: click toggle only (no mouseenter open).
  const desktopHandlers = isCompact
    ? {}
    : {
        onMouseEnter: showTip,
        onMouseLeave: hideTip,
        onFocus: showTip,
        onBlur: hideTip,
      };
  const compactHandlers = isCompact
    ? {
        onClick: toggleTip,
      }
    : {};

  // Custom trigger (sonic neighbor chips): hover/focus or compact tap.
  if (children) {
    return (
      <>
        <span
          ref={triggerRef}
          data-learn-more-trigger={id}
          className={`inline-flex max-w-full ${cursorClass} ${className}`.trim()}
          aria-describedby={open ? tipDomId : undefined}
          {...desktopHandlers}
          {...compactHandlers}
        >
          {children}
        </span>
        {tipPortal}
      </>
    );
  }

  return (
    <>
      {/* Default trigger: label + info icon */}
      <button
        type="button"
        ref={triggerRef}
        data-learn-more-trigger={id}
        className={`group/tip inline-flex items-center gap-1.5 min-w-0 max-w-full text-left rounded-md focus:outline-none focus-visible:ring-1 focus-visible:ring-apple-blue/50 ${cursorClass}`}
        aria-describedby={open ? tipDomId : undefined}
        {...desktopHandlers}
        {...compactHandlers}
      >
        <span className={`${labelClassName} truncate group-hover/tip:text-white transition-colors`}>
          {label}
        </span>
        <Info
          className="h-3.5 w-3.5 shrink-0 text-critic-text-muted opacity-50 group-hover/tip:opacity-90 transition-opacity"
          aria-hidden
        />
      </button>
      {tipPortal}
    </>
  );
}
